import React, { useState, useEffect, useRef, ChangeEvent } from "react";
import { trackEvent } from "../services/analytics"; 
import { useNavigate } from "react-router-dom";
import { isDevAccount } from "../services/devAccountMiddleware";
import { useFirebaseAuth } from "../hooks/useFirebaseAuth";
import "./AuthForm.css"; 
import axios from "axios";

/**
 * AuthForm Component
 * 
 * Dev Access: To enable dev mode, append ?dev=true to the URL
 * Example: https://spacepings.space/auth?dev=true
 */
const AuthForm: React.FC = () => {
  // Navigation hook for redirection
  const navigate = useNavigate();
  
  // Firebase auth hook
  const { user, loading: firebaseLoading, error: firebaseError, signIn: signInWithFirebase } = useFirebaseAuth();
  
  // Local component state
  const [error, setError] = useState<string>("");
  const [debugInfo, setDebugInfo] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [subscribeNewsletter, setSubscribeNewsletter] = useState<boolean>(false);
  const [isYearly, setIsYearly] = useState<boolean>(false);
  const [includeStarlink, setIncludeStarlink] = useState<boolean>(false);
  const [showDevLogin, setShowDevLogin] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [isReturningUser, setIsReturningUser] = useState<boolean>(false);
  const starsContainerRef = useRef<HTMLDivElement>(null);

  // Log debug information (only visible in development)
  const addDebugInfo = (info: string) => {
    if (process.env.NODE_ENV === "development") {
      console.debug(`[Auth Debug]: ${info}`);
      setDebugInfo((prev) => [...prev, info]);
    }
  };

  // Determine correct API URL based on environment
  const getApiBaseUrl = () => {
    // Use the configured API URL from config.ts
    return process.env.NODE_ENV === "production"
      ? (process.env.REACT_APP_API_BASE_URL || "https://spacepings.space")
      : (process.env.REACT_APP_API_BASE_URL || "http://localhost:5000");
  };

  // On component mount, validate environment variables and log debug info
  useEffect(() => {
    const apiUrl = getApiBaseUrl();

    addDebugInfo(`API Base URL: ${apiUrl}`);
    addDebugInfo(`Environment: ${process.env.NODE_ENV}`);

    // Check if we have any error from a previous auth attempt
    const urlParams = new URLSearchParams(window.location.search);
    const errorParam = urlParams.get("error");
    if (errorParam) {
      setError(decodeURIComponent(errorParam));
      addDebugInfo(`Error from URL param: ${errorParam}`);
      trackEvent("auth_error", JSON.stringify({ error: errorParam }));
    }
    
    // Set Firebase error if exists
    if (firebaseError) {
      setError(firebaseError);
      addDebugInfo(`Firebase error: ${firebaseError}`);
    }
    
    // Check for dev mode parameter
    const devParam = urlParams.get("dev");
    if (devParam === "true") {
      setShowDevLogin(true);
      addDebugInfo("Dev mode enabled via URL parameter");
    }
    
    // Check if user has previously logged in (returning user)
    const lastLogin = localStorage.getItem('lastLogin');
    const previousEmail = localStorage.getItem('previousEmail');
    const returningParam = urlParams.get("returning");
    
    if (lastLogin || previousEmail || returningParam === "true") {
      setIsReturningUser(true);
      addDebugInfo("Detected returning user");
    }
    
    // Auto-trigger sign-in if redirected from landing page with intent
    const returnTo = urlParams.get("returnTo");
    const action = urlParams.get("action");
    const autoSignIn = urlParams.get("auto");
    
    if ((returnTo && action) || autoSignIn === "true") {
      addDebugInfo("Auto-triggering sign-in from landing page redirect");
      console.log('Auto sign-in triggered', { returnTo, action, autoSignIn });
      
      // Remove auto parameter to prevent repeated triggers
      const newUrl = window.location.pathname + window.location.search.replace(/[?&]auto=true/, '');
      window.history.replaceState({}, '', newUrl);
      
      // Small delay to ensure component is fully mounted
      setTimeout(() => {
        console.log('About to trigger Google login...');
        handleGoogleLogin({ preventDefault: () => {}, stopPropagation: () => {} } as React.MouseEvent);
      }, 1000);
    }
  }, [firebaseError]);

  // Star background generation
  useEffect(() => {
    const generateStars = () => {
      const starsContainer = starsContainerRef.current;
      if (!starsContainer) return;
      starsContainer.innerHTML = ""; // Clear existing content

      // Create star layers with different properties
      const layers = [
        { count: 50, baseSize: 0.8, speed: 5 }, // Distant stars
        { count: 30, baseSize: 1.2, speed: 3 }, // Mid-range stars
        { count: 20, baseSize: 1.6, speed: 2 }, // Closer stars
      ];

      layers.forEach((layer, index) => {
        for (let i = 0; i < layer.count; i++) {
          const star = document.createElement("div");
          star.className = `auth-star layer-${index + 1}`;
          star.style.top = `${Math.random() * 100}%`;
          star.style.left = `${Math.random() * 100}%`;

          const size = layer.baseSize + Math.random() * layer.baseSize;
          star.style.width = `${size}px`;
          star.style.height = `${size}px`;
          star.style.animationDuration = `${layer.speed + Math.random() * layer.speed}s`;
          star.style.animationDelay = `${Math.random() * 5}s`;
          // Add subtle glow to some stars
          if (Math.random() > 0.7) {
            star.style.boxShadow = `0 0 ${size * 2}px rgba(255, 255, 255, 0.5)`;
          }
          starsContainer.appendChild(star);
        }
      });

      // Add glowing nebulae for extra cosmic effect
      for (let i = 0; i < 3; i++) {
        const nebula = document.createElement("div");
        nebula.className = "auth-nebula";
        nebula.style.top = `${Math.random() * 100}%`;
        nebula.style.left = `${Math.random() * 100}%`;
        nebula.style.width = `${50 + Math.random() * 50}px`;
        nebula.style.height = `${50 + Math.random() * 50}px`;
        nebula.style.animationDuration = `${10 + Math.random() * 10}s`;
        nebula.style.animationDelay = `${Math.random() * 5}s`;
        starsContainer.appendChild(nebula);
      }
    };

    generateStars();
    window.addEventListener("resize", generateStars);
    return () => window.removeEventListener("resize", generateStars);
  }, []);

  // Handle newsletter checkbox changes
  const handleNewsletterChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSubscribeNewsletter(e.target.checked);
    addDebugInfo(`Newsletter signup toggled: ${e.target.checked}`);
    // Convert object to JSON string
    trackEvent(
      "newsletter_toggle",
      JSON.stringify({ checked: e.target.checked }),
    );
  };
  
  // Toggle dev login form visibility
  const toggleDevLogin = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowDevLogin(!showDevLogin);
    addDebugInfo(`Dev login form toggled: ${!showDevLogin}`);
  };
  
  // Handle email login for dev users
  const handleDevLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");
    
    // Check if this is a recognized development account
    if (!isDevAccount(email)) {
      setError("This login method is for development accounts only");
      setIsSubmitting(false);
      return;
    }
    
    try {
      addDebugInfo(`Attempting dev login for: ${email}`);
      
      // For dev purposes, we're using a simplified auth process
      // In production, this would be a proper API call
      const API_BASE_URL = getApiBaseUrl();
      const response = await axios.post(`${API_BASE_URL}/auth/dev-login`, {
        email,
        password
      });
      
      if (response.data.token) {
        // Store the token and user info
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("email", email);
        localStorage.setItem("tier", "pro"); // Dev accounts get pro tier
        localStorage.setItem("lastLogin", new Date().toISOString());
        
        // Also store subscription data for dev access
        const subscriptionData = {
          plan: "pro",
          status: "active",
          features: ["all"],
          addOns: ["command-center"],
          expiryDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString() // 1 year from now
        };
        localStorage.setItem("subscriptionData", JSON.stringify(subscriptionData));
        
        addDebugInfo("Dev login successful");
        trackEvent("auth_success", JSON.stringify({ method: "dev-email", email }));
        
        // Redirect to dashboard
        navigate("/dashboard");
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (err: any) {
      // For development accounts, we can mock a successful login if the API isn't available
      if (email === "stackcurious@gmail.com" && password === "devpassword") {
        // Generate a mock token
        const mockToken = `dev_${btoa(email)}_${Date.now()}`;
        
        // Store the token and user info
        localStorage.setItem("token", mockToken);
        localStorage.setItem("email", email);
        localStorage.setItem("tier", "pro"); // Dev accounts get pro tier
        localStorage.setItem("lastLogin", new Date().toISOString());
        
        // Also store subscription data for dev access
        const subscriptionData = {
          plan: "pro",
          status: "active",
          features: ["all"],
          addOns: ["command-center"],
          expiryDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString() // 1 year from now
        };
        localStorage.setItem("subscriptionData", JSON.stringify(subscriptionData));
        
        addDebugInfo("Dev login successful (mock mode)");
        trackEvent("auth_success", JSON.stringify({ method: "dev-email-mock", email }));
        
        // Redirect to dashboard
        navigate("/dashboard");
        return;
      }
      
      const errorMessage = err.response?.data?.message || err.message || "Login failed";
      addDebugInfo(`Dev login error: ${errorMessage}`);
      setError(errorMessage);
      trackEvent("auth_failed", JSON.stringify({ method: "dev-email", error: errorMessage }));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Google login with Firebase
  const handleGoogleLogin = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    console.log('Firebase Google login initiated!');
    
    // Prevent multiple simultaneous calls
    if (isSubmitting) {
      console.log('Already submitting, ignoring duplicate call');
      return;
    }
    
    try {
      setIsSubmitting(true);
      setError("");

      addDebugInfo("Starting Firebase Google login flow");
      console.log('Firebase auth starting...');
      
      // Save pricing selection to localStorage before auth
      const plan = includeStarlink ? 'bundle' : 'base';
      const billing = isYearly ? 'yearly' : 'monthly';
      const checkoutData = { plan, billing, timestamp: Date.now() };
      
      localStorage.setItem('pendingCheckout', JSON.stringify(checkoutData));
      localStorage.setItem('pendingCheckout_backup', JSON.stringify(checkoutData));
      localStorage.setItem('authflow_pricing', JSON.stringify(checkoutData));
      
      addDebugInfo(`Saved pending checkout: plan=${plan}, billing=${billing}`);

      // Track auth attempt
      trackEvent("auth_attempt", JSON.stringify({ method: "firebase", plan, billing }));
      
      // Initiate Firebase sign in - the useFirebaseAuth hook handles the rest
      await signInWithFirebase();
      
    } catch (err: any) {
      console.error('Firebase login error:', err);
      const errorMessage = err.message || "Google Sign-In failed";
      addDebugInfo(`Firebase Sign-In error: ${errorMessage}`);
      setError(errorMessage);
      trackEvent("auth_failed", JSON.stringify({ method: "firebase-google", error: errorMessage }));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle GitHub login with proper redirect to backend /auth/github endpoint
  /* Commented out GitHub login functionality for now
  const handleGithubLogin = (e: React.MouseEvent) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);
      setError(""); // Clear any previous errors

      // Clear existing token and email to ensure a fresh login
      localStorage.removeItem("token");
      localStorage.removeItem("email");
      addDebugInfo("Cleared existing token and email from local storage");

      const API_BASE_URL = getApiBaseUrl();

      addDebugInfo("Starting GitHub login flow");
      addDebugInfo(`API Base URL: ${API_BASE_URL}`);

      // Add essential parameters to ensure proper OAuth flow
      const authParams = new URLSearchParams({
        newsletter: subscribeNewsletter ? "yes" : "no",
        timestamp: new Date().getTime().toString(), // Prevent caching issues
      });

      // Ensure the correct route path (/auth/github, not /api/auth/github)
      const authUrl = `${API_BASE_URL}/auth/github?${authParams.toString()}`;
      addDebugInfo(`Redirecting to: ${authUrl}`);

      // Convert object to JSON string
      trackEvent("auth_attempt", JSON.stringify({ method: "github" }));
      window.location.href = authUrl;
    } catch (err: any) {
      const errorMessage = err.message || "GitHub Sign-In failed";
      addDebugInfo(`GitHub Sign-In error: ${errorMessage}`);
      setError(errorMessage);
      setIsSubmitting(false);
      // Convert object to JSON string
      trackEvent(
        "auth_failed",
        JSON.stringify({ method: "github", error: errorMessage }),
      );
    }
  };
  */

  // Component JSX (CSS styles and UI remain the same)
  return (
    <main className="auth-container" aria-label="Authentication form">

      <div className="auth-spaceship-container">
        <svg
          className="auth-spaceship"
          viewBox="0 0 100 50"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M50 5 L90 25 L50 45 L10 25 Z M50 5 L70 25 L50 45 L30 25 Z M50 15 L80 25 L50 35 L20 25 Z"
            fill="none"
            stroke="#ffffff"
            strokeWidth="2"
            strokeOpacity="0.8"
          />
          <path
            d="M50 45 L45 50 L55 50 Z"
            fill="rgba(255, 165, 0, 0.5)"
            stroke="#ffa500"
            strokeWidth="1"
          />
          <circle cx="50" cy="25" r="5" fill="#00ff00" opacity="0.5" />
        </svg>
      </div>

      <div className="auth-stars-container" ref={starsContainerRef} />
      <div className="auth-cosmic-glow" />
      <div className="auth-shooting-star"></div>
      <div className="auth-shooting-star"></div>
      <div className="auth-shooting-star"></div>

      <section className="auth-card">
        <div className="auth-cosmic-objects">
          <div className="auth-planet" aria-hidden="true" />
          <div className="auth-moon" aria-hidden="true" />
          <div className="auth-orbit" aria-hidden="true">
            <div className="auth-satellite" />
          </div>
        </div>

        <div className="auth-form">
          {/* Mode Toggle Tabs */}
          <div className="auth-mode-tabs">
            <button
              className={`auth-tab ${!isReturningUser ? 'active' : ''}`}
              onClick={() => setIsReturningUser(false)}
              style={{
                flex: 1,
                padding: '12px 16px',
                background: !isReturningUser ? 'linear-gradient(135deg, #4338ca, #6366f1)' : 'transparent',
                border: !isReturningUser ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                color: '#fff',
                borderRadius: '8px 0 0 8px',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.9rem',
                transition: 'all 0.3s ease'
              }}
            >
              🚀 Sign Up
            </button>
            <button
              className={`auth-tab ${isReturningUser ? 'active' : ''}`}
              onClick={() => setIsReturningUser(true)}
              style={{
                flex: 1,
                padding: '12px 16px',
                background: isReturningUser ? 'linear-gradient(135deg, #4338ca, #6366f1)' : 'transparent',
                border: isReturningUser ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                color: '#fff',
                borderRadius: '0 8px 8px 0',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.9rem',
                transition: 'all 0.3s ease'
              }}
            >
              🔑 Sign In
            </button>
          </div>

          <h2 className="auth-title" style={{ marginTop: '24px', marginBottom: '8px' }}>
            {isReturningUser ? 'Welcome Back, Space Explorer' : 'Begin Your Cosmic Adventure'}
          </h2>
          <h3 className="auth-subtitle" style={{ marginBottom: '24px' }}>
            {isReturningUser ? 'Continue tracking satellites and launches' : 'Get real-time satellite alerts & launch updates'}
          </h3>
          
          {!isReturningUser && (
          <div className="pricing-badge" style={{ marginBottom: '20px' }}>
            <div className="pricing-toggle">
              <span 
                className={`toggle-option ${!isYearly ? 'active' : ''}`}
                onClick={() => setIsYearly(false)}
              >
                Monthly
              </span>
              <span className="toggle-slider-container">
                <span className={`toggle-slider ${isYearly ? 'yearly' : ''}`} onClick={() => setIsYearly(!isYearly)}></span>
              </span>
              <span 
                className={`toggle-option ${isYearly ? 'active' : ''}`}
                onClick={() => setIsYearly(true)}
              >
                Yearly
                <span className="pricing-save">Save 33%</span>
              </span>
            </div>
            
            {/* Base pricing */}
            <div className="pricing-badge-content">
              <span className="pricing-amount">
                ${isYearly ? 
                  (includeStarlink ? '59.98' : '39.99') : 
                  (includeStarlink ? '6.98' : '4.99')}
              </span>
              <span className="pricing-period">/{isYearly ? 'year' : 'month'}</span>
            </div>
            
            {/* Starlink addon toggle */}
            <div style={{ marginTop: '12px', padding: '8px', background: 'rgba(139, 92, 246, 0.1)', borderRadius: '8px', border: '1px solid rgba(139, 92, 246, 0.3)' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.85rem' }}>
                <input
                  type="checkbox"
                  checked={includeStarlink}
                  onChange={(e) => setIncludeStarlink(e.target.checked)}
                  style={{ marginRight: '8px' }}
                />
                <span style={{ marginRight: '4px' }}>🛰️</span>
                <span style={{ color: '#c084fc', fontWeight: 500 }}>
                  Add Starlink Command Center
                </span>
                <span style={{ marginLeft: '8px', color: '#93c5fd' }}>
                  (+${isYearly ? '19.99' : '1.99'})
                </span>
              </label>
              {includeStarlink && (
                <div style={{ fontSize: '0.75rem', color: '#93c5fd', marginTop: '4px' }}>
                  Track your personal satellite!
                </div>
              )}
            </div>
            
            <span className="pricing-trial">{isYearly ? 'Includes 2 months free' : 'Start with 1 month free'}</span>
          </div>
          )}

          {error && (
            <div className="auth-error" role="alert" style={{ marginBottom: '20px' }}>
              <span className="auth-error-icon" aria-hidden="true">
                ⚠️
              </span>
              <p className="auth-error-message">{error}</p>
            </div>
          )}

          <div className="auth-fields">
            {/* Google Login Button */}
            <button
              type="button"
              className="google-button cosmic-auth-button primary-button"
              onClick={handleGoogleLogin}
              disabled={isSubmitting || firebaseLoading}
              aria-label={isReturningUser ? "Sign in with Google" : "Sign up with Google"}
              style={{
                marginBottom: "20px",
                position: "relative",
                zIndex: 1000,
                cursor: (isSubmitting || firebaseLoading) ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                padding: "16px 24px",
                background: (isSubmitting || firebaseLoading) ? 
                  "linear-gradient(135deg, #6b7280, #9ca3af)" : 
                  "linear-gradient(135deg, #ea4335, #fbbc04, #34a853, #4285f4)",
                backgroundSize: "400% auto",
                color: "#ffffff",
                border: "2px solid rgba(255, 255, 255, 0.1)",
                borderRadius: "12px",
                fontWeight: 700,
                fontSize: "16px",
                letterSpacing: "0.5px",
                boxShadow: isSubmitting ? 
                  "0 4px 15px rgba(0, 0, 0, 0.1)" :
                  "0 6px 20px rgba(0, 0, 0, 0.15), 0 0 30px rgba(66, 133, 244, 0.2)",
                opacity: isSubmitting ? 0.7 : 1,
                transition: "all 0.4s cubic-bezier(0.2, 0.8, 0.2, 1)",
                overflow: "hidden",
                textShadow: "0 1px 2px rgba(0, 0, 0, 0.3)"
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.boxShadow = "0 10px 30px rgba(0, 0, 0, 0.2), 0 0 40px rgba(66, 133, 244, 0.4)";
                  e.currentTarget.style.transform = "translateY(-4px) scale(1.02)";
                  e.currentTarget.style.backgroundPosition = "right center";
                  e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.3)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.boxShadow = "0 6px 20px rgba(0, 0, 0, 0.15), 0 0 30px rgba(66, 133, 244, 0.2)";
                  e.currentTarget.style.transform = "";
                  e.currentTarget.style.backgroundPosition = "";
                  e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.1)";
                }
              }}
            >
              <span
                className="google-icon"
                aria-hidden="true"
                style={{ 
                  display: "inline-flex",
                  marginRight: "12px",
                  background: "rgba(255, 255, 255, 0.9)",
                  borderRadius: "4px",
                  padding: "4px",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)"
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                >
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
              </span>
              <span style={{ fontSize: "16px", fontWeight: 700 }}>
                {isSubmitting ? (
                  <>
                    <span style={{ display: "inline-block", animation: "spin 1s linear infinite", marginRight: "8px" }}>⏳</span>
                    Connecting...
                  </>
                ) : (
                  <>
                    {isReturningUser ? "🔑 Sign In with Google" : "🚀 Start Free with Google"}
                  </>
                )}
              </span>
            </button>
            
            {/* Clear Instructions */}
            <div style={{ 
              background: "rgba(59, 130, 246, 0.1)", 
              border: "1px solid rgba(59, 130, 246, 0.3)", 
              borderRadius: "8px", 
              padding: "12px", 
              marginBottom: "16px",
              textAlign: "center" 
            }}>
              <div style={{ fontSize: "0.85rem", color: "#93c5fd", lineHeight: "1.4" }}>
                {isReturningUser ? (
                  <>
                    <strong>Returning User:</strong> Click above to securely sign in with your Google account
                  </>
                ) : (
                  <>
                    <strong>New User:</strong> Click above to create your account and start your free trial
                  </>
                )}
              </div>
            </div>
            
            {/* Dev Email Login Form - Hidden by default */}
            {showDevLogin && (
              <form onSubmit={handleDevLogin} style={{ marginBottom: "20px" }}>
                <div style={{ marginBottom: "16px" }}>
                  <label 
                    htmlFor="dev-email" 
                    style={{ 
                      display: "block", 
                      marginBottom: "6px", 
                      fontSize: "0.9rem", 
                      color: "#a0c0ff", 
                      fontWeight: 500 
                    }}
                  >
                    Development Email
                  </label>
                  <input
                    id="dev-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="stackcurious@gmail.com"
                    required
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      borderRadius: "8px",
                      background: "rgba(30, 41, 59, 0.5)",
                      border: "1px solid rgba(148, 163, 184, 0.2)",
                      color: "#fff",
                      fontSize: "1rem",
                      transition: "all 0.3s ease",
                    }}
                  />
                </div>
                
                <div style={{ marginBottom: "16px" }}>
                  <label 
                    htmlFor="dev-password" 
                    style={{ 
                      display: "block", 
                      marginBottom: "6px", 
                      fontSize: "0.9rem", 
                      color: "#a0c0ff", 
                      fontWeight: 500 
                    }}
                  >
                    Development Password
                  </label>
                  <input
                    id="dev-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="devpassword"
                    required
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      borderRadius: "8px",
                      background: "rgba(30, 41, 59, 0.5)",
                      border: "1px solid rgba(148, 163, 184, 0.2)",
                      color: "#fff",
                      fontSize: "1rem",
                      transition: "all 0.3s ease",
                    }}
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: "8px",
                    background: "linear-gradient(135deg, #059669, #10b981)",
                    color: "#fff",
                    border: "none",
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                  }}
                >
                  {isSubmitting ? "Signing In..." : "Development Sign In"}
                </button>
                
                <p style={{ 
                  fontSize: "0.8rem", 
                  color: "#64748b", 
                  marginTop: "12px", 
                  textAlign: "center" 
                }}>
                  For authorized development accounts only
                </p>
              </form>
            )}

            {/* GitHub Login Button - Removed */}
            {/* 
            <div className="auth-separator">
              <span className="auth-separator-line" aria-hidden="true" />
              <span className="auth-separator-text">or</span>
              <span className="auth-separator-line" aria-hidden="true" />
            </div>

            <button
              type="button"
              className="github-button"
              onClick={handleGithubLogin}
              disabled={isSubmitting}
              aria-label="Sign in with GitHub"
              style={{
                marginTop: "16px",
                position: "relative",
                zIndex: 10,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                padding: "14px 28px",
                background: "linear-gradient(135deg, #764ba2, #667eea, #818cf8, #667eea)",
                backgroundSize: "200% auto",
                color: "#ffffff",
                border: "none",
                borderRadius: "12px",
                fontWeight: 600,
                fontSize: "16px",
                boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
                pointerEvents: isSubmitting ? "none" : "auto",
                opacity: isSubmitting ? 0.6 : 1,
                transition: "all 0.4s cubic-bezier(0.2, 0.8, 0.2, 1)",
                overflow: "hidden",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = "0 10px 20px rgba(0, 0, 0, 0.2), 0 0 20px rgba(118, 75, 162, 0.4)";
                e.currentTarget.style.transform = "translateY(-3px) scale(1.02)";
                e.currentTarget.style.backgroundPosition = "right center";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "0 4px 15px rgba(0, 0, 0, 0.2)";
                e.currentTarget.style.transform = "";
                e.currentTarget.style.backgroundPosition = "";
              }}
            >
              <span
                className="github-icon"
                aria-hidden="true"
                style={{ display: "inline-flex" }}
              >
                <svg
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                >
                  <path
                    d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"
                    fill="currentColor"
                  />
                </svg>
              </span>
              <span style={{ marginLeft: "8px" }}>
                {isSubmitting ? "Processing..." : "Start Free with GitHub"}
              </span>
              <svg style={{ width: "18px", height: "18px", marginLeft: "8px", transition: "transform 0.3s ease" }} viewBox="0 0 20 20" fill="currentColor" className="button-arrow-icon">
                <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
            */}

            {/* Newsletter Signup Checkbox */}
            <div
              className="newsletter-checkbox"
              style={{
                marginTop: "24px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <input
                type="checkbox"
                id="newsletter-signup"
                checked={subscribeNewsletter}
                onChange={handleNewsletterChange}
                disabled={isSubmitting}
                style={{ marginRight: "8px", width: "18px", height: "18px" }}
              />
              <label
                htmlFor="newsletter-signup"
                style={{ fontSize: "1rem", color: "#fff" }}
              >
                Send me space updates and satellite news
              </label>
            </div>
          </div>

          {/* Pricing info for new users */}
          {!isReturningUser && (
            <div style={{
              marginTop: "16px",
              padding: "12px",
              background: "rgba(34, 197, 94, 0.1)",
              border: "1px solid rgba(34, 197, 94, 0.3)",
              borderRadius: "8px",
              textAlign: "center",
              fontSize: "0.85rem",
              color: "#86efac",
              lineHeight: "1.4"
            }}>
              <div style={{ fontWeight: 600, marginBottom: "4px" }}>
                💳 {isYearly ? "Annual Billing" : "Monthly Billing"}
              </div>
              <div>
                {isYearly 
                  ? "Pay upfront, get 2 months free (10 months for the price of 8)"
                  : "Start free, cancel anytime - card required, billed after trial"}
              </div>
            </div>
          )}
          
          {/* Security note for returning users */}
          {isReturningUser && (
            <div style={{
              marginTop: "16px",
              padding: "12px",
              background: "rgba(59, 130, 246, 0.1)",
              border: "1px solid rgba(59, 130, 246, 0.3)",
              borderRadius: "8px",
              textAlign: "center",
              fontSize: "0.85rem",
              color: "#93c5fd",
              lineHeight: "1.4"
            }}>
              <div style={{ fontWeight: 600, marginBottom: "4px" }}>
                🔒 Secure Authentication
              </div>
              <div>
                We'll redirect you to Google for secure sign-in, then bring you back to your dashboard
              </div>
            </div>
          )}

          {/* Add CSS animation for loading spinner */}
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            .auth-mode-tabs {
              display: flex;
              margin-bottom: 0;
              border-radius: 8px;
              overflow: hidden;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            }
          `}</style>

          {/* Debug Section (Visible only during development) */}
          {process.env.NODE_ENV === "development" && debugInfo.length > 0 && (
            <div
              className="auth-debug"
              style={{
                marginTop: "20px",
                padding: "10px",
                background: "#1a1a2e",
                borderRadius: "4px",
                fontSize: "12px",
                maxHeight: "200px",
                overflowY: "auto",
                color: "#ccc",
              }}
            >
              <h4 style={{ margin: "0 0 8px 0", color: "#6366F1" }}>
                Debug Info:
              </h4>
              <ul style={{ margin: 0, paddingLeft: "20px" }}>
                {debugInfo.map((info, index) => (
                  <li key={index}>{info}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default AuthForm;
